import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { theme } from '@resistapp/client/components/shared/theme';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { copyTextToClipboard } from '@resistapp/client/utils/clipboard';
import { CreateShipmentRequest, zCreateShipmentRequest } from '@resistapp/common/api-types';
import { isEuropeanUnionCountry } from '@resistapp/common/shipment-countries';
import { CountryByAlpha3, ShipmentStatus, zShipmentStatus } from '@resistapp/common/types';
import { chain } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Path, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { getPackageSizeWarningState, isAboveAdditionalChargeVolume } from './shipment-package-size';

const SAMPLE_DNA_ITEM_DESCRIPTION = '1.5 ml micro-centrifuge tubes with environmental DNA sample';
const LEGACY_SAMPLE_DNA_ITEM_DESCRIPTION = '1.5 ml eppendorf tubes with environmental DNA sample';

const DEFAULT_DNA_SIZE_GUIDELINES = [
  { minCount: 1, maxCount: 100, maxVolumeCm3: 5000, maxWeightKg: 3.0 },
  { minCount: 101, maxCount: 200, maxVolumeCm3: 9000, maxWeightKg: 4.5 },
  { minCount: 201, maxCount: 300, maxVolumeCm3: 14000, maxWeightKg: 6.0 },
  { minCount: 301, maxCount: 500, maxVolumeCm3: 28000, maxWeightKg: 8.0 },
] as const;

function isDefaultDnaDescription(description: string): boolean {
  const normalizedDescription = description.trim().toLowerCase();
  return [SAMPLE_DNA_ITEM_DESCRIPTION, LEGACY_SAMPLE_DNA_ITEM_DESCRIPTION].some(
    defaultDescription => normalizedDescription === defaultDescription.toLowerCase(),
  );
}

function getDefaultDnaItemCount(items: Array<{ description: string; count: number }>): number {
  return items.reduce((sum, item) => (isDefaultDnaDescription(item.description) ? sum + item.count : sum), 0);
}

function getGuidelineForCount(defaultDnaItemCount: number) {
  return DEFAULT_DNA_SIZE_GUIDELINES.find(
    guideline => defaultDnaItemCount >= guideline.minCount && defaultDnaItemCount <= guideline.maxCount,
  );
}

function getVolumeCm3(values: {
  packageLengthCm: number | null | undefined;
  packageWidthCm: number | null | undefined;
  packageHeightCm: number | null | undefined;
}): number | null {
  if (values.packageLengthCm == null || values.packageWidthCm == null || values.packageHeightCm == null) {
    return null;
  }
  const volume = values.packageLengthCm * values.packageWidthCm * values.packageHeightCm;
  return Number.isFinite(volume) ? volume : null;
}

function getDefaultDnaSizeCheck(values: {
  items: Array<{ description: string; count: number }>;
  packageWeightKg: number | null | undefined;
  packageLengthCm: number | null | undefined;
  packageWidthCm: number | null | undefined;
  packageHeightCm: number | null | undefined;
}) {
  const defaultDnaItemCount = getDefaultDnaItemCount(values.items);
  const guideline = getGuidelineForCount(defaultDnaItemCount);
  const volumeCm3 = getVolumeCm3(values);
  const isWeightAboveGuideline = guideline ? (values.packageWeightKg ?? 0) > guideline.maxWeightKg : false;
  const isVolumeAboveGuideline = guideline ? (volumeCm3 ?? 0) > guideline.maxVolumeCm3 : false;
  const isAboveAdditionalChargeThreshold = isAboveAdditionalChargeVolume(volumeCm3);

  return {
    defaultDnaItemCount,
    guideline,
    volumeCm3,
    isWeightAboveGuideline,
    isVolumeAboveGuideline,
    isAboveAdditionalChargeThreshold,
    isAboveGuideline: isWeightAboveGuideline || isVolumeAboveGuideline,
  };
}

const zShipmentFormValues = z.intersection(
  zCreateShipmentRequest,
  z.object({
    status: zShipmentStatus.default(ShipmentStatus.REQUESTED),
  }),
);

export type ShipmentFormValues = z.infer<typeof zShipmentFormValues>;

export interface ShipmentFormOrganisation {
  id: number;
  name: string;
  country?: string | null;
  pickupAddressLine1?: string | null;
  pickupAddressLine2?: string | null;
  pickupAddressLine3?: string | null;
  pickupPostalCode?: string | null;
  pickupCity?: string | null;
  taxId?: string | null;
}

export interface ShipmentFormLabWorkbook {
  id: string;
  name: string;
}

export function ShipmentForm({
  mode,
  defaultValues,
  countriesByAlpha3,
  organisationOptions,
  labWorkbookOptions,
  submitLabel,
  isSubmitting,
  allowStatusEdit,
  disableOrganisationSelect,
  disableLabWorkbookSelect,
  xlShipmentsAuthorised = false,
  readOnly,
  copyReadOnlyValues,
  extraButtons,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  defaultValues: ShipmentFormValues;
  countriesByAlpha3?: CountryByAlpha3;
  organisationOptions: ShipmentFormOrganisation[];
  labWorkbookOptions?: ShipmentFormLabWorkbook[];
  submitLabel: string;
  isSubmitting?: boolean;
  allowStatusEdit?: boolean;
  disableOrganisationSelect?: boolean;
  disableLabWorkbookSelect?: boolean;
  xlShipmentsAuthorised?: boolean;
  readOnly?: boolean;
  copyReadOnlyValues?: boolean;
  extraButtons?: React.ReactNode;
  onSubmit: (values: ShipmentFormValues) => Promise<void>;
}) {
  const {
    control,
    formState: { errors, isSubmitting: formIsSubmitting },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<ShipmentFormValues>({
    resolver: zodResolver(zShipmentFormValues),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });
  const [pendingLargeBoxValues, setPendingLargeBoxValues] = useState<ShipmentFormValues | null>(null);
  const [showAdditionalChargeWarning, setShowAdditionalChargeWarning] = useState(false);
  const [isConfirmingSubmit, setIsConfirmingSubmit] = useState(false);
  const {
    isOpen: isLargeBoxDialogOpen,
    onOpen: onOpenLargeBoxDialog,
    onClose: onCloseLargeBoxDialog,
  } = useDisclosure();
  const largeBoxCancelRef = useRef<HTMLButtonElement>(null);
  const showToast = useToastWithoutDebounce();

  const watchedOrganisationId = watch('organisationId');
  const watchedPickupCountry = watch('pickupCountry');
  const watchedItems = watch('items');
  const watchedWeightKg = watch('packageWeightKg');
  const watchedLength = watch('packageLengthCm');
  const watchedWidth = watch('packageWidthCm');
  const watchedHeight = watch('packageHeightCm');

  const volumeCm3 = useMemo(() => {
    return getVolumeCm3({
      packageLengthCm: watchedLength,
      packageWidthCm: watchedWidth,
      packageHeightCm: watchedHeight,
    });
  }, [watchedHeight, watchedLength, watchedWidth]);

  const defaultDnaSizeCheck = useMemo(() => {
    return getDefaultDnaSizeCheck({
      items: watchedItems,
      packageWeightKg: watchedWeightKg,
      packageLengthCm: watchedLength,
      packageWidthCm: watchedWidth,
      packageHeightCm: watchedHeight,
    });
  }, [watchedHeight, watchedItems, watchedLength, watchedWeightKg, watchedWidth]);

  const shouldShowDefaultDnaGuidance = defaultDnaSizeCheck.defaultDnaItemCount > 0;
  const isTaxIdRequired = !isEuropeanUnionCountry(watchedPickupCountry);

  const handleLargeBoxDialogClose = () => {
    setPendingLargeBoxValues(null);
    setShowAdditionalChargeWarning(false);
    onCloseLargeBoxDialog();
  };

  const handleFormSubmit = async (values: ShipmentFormValues) => {
    const sizeCheck = getDefaultDnaSizeCheck({
      items: values.items,
      packageWeightKg: values.packageWeightKg,
      packageLengthCm: values.packageLengthCm,
      packageWidthCm: values.packageWidthCm,
      packageHeightCm: values.packageHeightCm,
    });
    const warningState = getPackageSizeWarningState({
      isAboveGuideline: sizeCheck.isAboveGuideline,
      isAboveAdditionalChargeThreshold: sizeCheck.isAboveAdditionalChargeThreshold,
      xlShipmentsAuthorised,
    });

    if (mode === 'create' && !readOnly && warningState.shouldShowConfirmation) {
      setPendingLargeBoxValues(values);
      setShowAdditionalChargeWarning(warningState.showAdditionalChargeWarning);
      onOpenLargeBoxDialog();
      return;
    }

    await onSubmit(values);
  };

  const countryOptions = useMemo(() => {
    if (!countriesByAlpha3) {
      return [] as Array<{ alpha3: string; name: string }>;
    }
    return chain(countriesByAlpha3)
      .map((country, alpha3) => ({ alpha3, name: country.name }))
      .sortBy(country => country.name.toLowerCase())
      .value();
  }, [countriesByAlpha3]);
  const selectedOrganisation = organisationOptions.find(option => option.id === defaultValues.organisationId);
  const selectedLabWorkbook = labWorkbookOptions?.find(option => option.id === defaultValues.labWorkbookId);
  const selectedCountry = countryOptions.find(option => option.alpha3 === defaultValues.pickupCountry);

  const getCopyFieldProps = (value: unknown, label: string) => {
    const normalizedValue = value == null ? '' : String(value).trim();
    if (!readOnly || !copyReadOnlyValues || normalizedValue.length === 0) {
      return {};
    }
    return {
      'data-copy-label': label,
      'data-copy-value': normalizedValue,
      title: `Copy ${label.toLowerCase()}`,
    };
  };

  const handleCopyableFieldClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!readOnly || !copyReadOnlyValues || !(event.target instanceof Element)) {
      return;
    }
    const field = event.target.closest<HTMLElement>('[data-copy-value]');
    const value = field?.dataset.copyValue;
    if (!field || !value) {
      return;
    }

    const label = field.dataset.copyLabel ?? 'Value';
    void (async () => {
      try {
        await copyTextToClipboard(value);
        showToast({
          title: `${label} copied`,
          status: 'success',
          duration: 2500,
        });
      } catch (copyError) {
        showToast({
          title: 'Failed to copy value',
          description: copyError instanceof Error ? copyError.message : 'Please try again later.',
          status: 'error',
          duration: 5000,
        });
      }
    })();
  };

  useEffect(() => {
    if (mode !== 'create' || !watchedOrganisationId) {
      return;
    }
    const organisation = organisationOptions.find(option => option.id === watchedOrganisationId);
    if (!organisation) {
      return;
    }

    setValue('pickupCountry', organisation.country ?? null);
    setValue('pickupAddressLine1', organisation.pickupAddressLine1 ?? null);
    setValue('pickupAddressLine2', organisation.pickupAddressLine2 ?? null);
    setValue('pickupAddressLine3', organisation.pickupAddressLine3 ?? null);
    setValue('pickupPostalCode', organisation.pickupPostalCode ?? null);
    setValue('pickupCity', organisation.pickupCity ?? null);
    setValue('taxId', organisation.taxId ?? null);
  }, [mode, organisationOptions, setValue, watchedOrganisationId]);

  const nullableTextRegister = (field: Path<ShipmentFormValues>) =>
    register(field, {
      setValueAs: value => {
        if (value == null) {
          return null;
        }
        const trimmed = String(value).trim();
        return trimmed.length === 0 ? null : trimmed;
      },
    });

  const nullableNumberRegister = (field: Path<ShipmentFormValues>) =>
    register(field, {
      setValueAs: value => {
        if (value === '' || value == null) {
          return null;
        }
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : null;
      },
    });

  return (
    <>
      <form
        onSubmit={event => {
          if (readOnly) {
            event.preventDefault();
            return;
          }
          void handleSubmit(handleFormSubmit)(event);
        }}
      >
        <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0 }}>
          <Stack
            spacing={4}
            bg="white"
            p={theme.spacing[4]}
            borderRadius={6}
            boxShadow="sm"
            onClick={handleCopyableFieldClick}
            sx={
              readOnly && copyReadOnlyValues
                ? {
                    '& [data-copy-value]': {
                      cursor: 'copy',
                      transition: 'background-color 0.15s ease',
                    },
                    '& [data-copy-value]:hover': {
                      backgroundColor: 'blue.50',
                    },
                    '& [data-copy-value] input, & [data-copy-value] select': {
                      pointerEvents: 'none',
                    },
                  }
                : undefined
            }
          >
            <Text color="gray.600">
              Fill out shipment pickup details below. Pickup requests should ideally target Monday to Wednesday to
              minimize weekend storage and transport issues.
            </Text>

            <FormControl
              isInvalid={!!errors.organisationId}
              isRequired={true}
              {...getCopyFieldProps(
                selectedOrganisation
                  ? `${selectedOrganisation.name} (${selectedOrganisation.id})`
                  : defaultValues.organisationId,
                'Organisation',
              )}
            >
              <FormLabel>Organisation</FormLabel>
              <Select
                isDisabled={disableOrganisationSelect || organisationOptions.length <= 1}
                {...register('organisationId', {
                  setValueAs: value => Number(value),
                })}
              >
                {organisationOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name} ({option.id})
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.organisationId?.message}</FormErrorMessage>
            </FormControl>

            {labWorkbookOptions && labWorkbookOptions.length > 0 ? (
              <FormControl
                isInvalid={!!errors.labWorkbookId}
                isRequired={true}
                {...getCopyFieldProps(
                  selectedLabWorkbook
                    ? `${selectedLabWorkbook.name} (${selectedLabWorkbook.id})`
                    : defaultValues.labWorkbookId,
                  'Lab workbook',
                )}
              >
                <FormLabel>Lab workbook</FormLabel>
                <Select
                  isDisabled={disableLabWorkbookSelect || labWorkbookOptions.length <= 1}
                  {...register('labWorkbookId', {
                    setValueAs: value => {
                      if (value === '' || value == null) {
                        return null;
                      }
                      return String(value);
                    },
                  })}
                >
                  {labWorkbookOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name} ({option.id})
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.labWorkbookId?.message}</FormErrorMessage>
              </FormControl>
            ) : null}

            {allowStatusEdit ? (
              <FormControl isInvalid={!!errors.status} {...getCopyFieldProps(defaultValues.status, 'Status')}>
                <FormLabel>Status</FormLabel>
                <Select {...register('status')}>
                  {Object.values(ShipmentStatus).map(status => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.status?.message}</FormErrorMessage>
              </FormControl>
            ) : null}

            <Divider />

            <Text fontWeight={600}>Contact</Text>
            <HStack align="flex-start" spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
              <FormControl
                isInvalid={!!errors.contactName}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.contactName, 'Contact name')}
              >
                <FormLabel>Contact name</FormLabel>
                <Input {...register('contactName')} />
                <FormErrorMessage>{errors.contactName?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.contactEmail}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.contactEmail, 'Contact email')}
              >
                <FormLabel>Contact email</FormLabel>
                <Input type="email" {...register('contactEmail')} />
                <FormErrorMessage>{errors.contactEmail?.message}</FormErrorMessage>
              </FormControl>
            </HStack>

            <FormControl
              isInvalid={!!errors.contactTelephoneNumber}
              isRequired={true}
              {...getCopyFieldProps(defaultValues.contactTelephoneNumber, 'Contact phone number')}
            >
              <FormLabel>Contact phone number</FormLabel>
              <Input {...nullableTextRegister('contactTelephoneNumber')} />
              <FormErrorMessage>{errors.contactTelephoneNumber?.message}</FormErrorMessage>
            </FormControl>

            <Divider />

            <Text fontWeight={600}>Pickup location</Text>

            <HStack align="flex-start" spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
              <FormControl
                isInvalid={!!errors.pickupCountry}
                isRequired={true}
                {...getCopyFieldProps(selectedCountry?.name ?? defaultValues.pickupCountry, 'Pickup country')}
              >
                <FormLabel>Country</FormLabel>
                <Select {...nullableTextRegister('pickupCountry')}>
                  <option value="">Select country</option>
                  {countryOptions.map(option => (
                    <option key={option.alpha3} value={option.alpha3}>
                      {option.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.pickupCountry?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.pickupCity}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.pickupCity, 'Pickup city')}
              >
                <FormLabel>City</FormLabel>
                <Input {...nullableTextRegister('pickupCity')} />
                <FormErrorMessage>{errors.pickupCity?.message}</FormErrorMessage>
              </FormControl>
            </HStack>

            <FormControl
              isInvalid={!!errors.pickupAddressLine1}
              isRequired={true}
              {...getCopyFieldProps(defaultValues.pickupAddressLine1, 'Address line 1')}
            >
              <FormLabel>Address line 1</FormLabel>
              <Input {...nullableTextRegister('pickupAddressLine1')} />
              <FormErrorMessage>{errors.pickupAddressLine1?.message}</FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={!!errors.pickupAddressLine2}
              {...getCopyFieldProps(defaultValues.pickupAddressLine2, 'Address line 2')}
            >
              <FormLabel>Address line 2 (optional)</FormLabel>
              <Input {...nullableTextRegister('pickupAddressLine2')} />
              <FormErrorMessage>{errors.pickupAddressLine2?.message}</FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={!!errors.pickupAddressLine3}
              {...getCopyFieldProps(defaultValues.pickupAddressLine3, 'Address line 3')}
            >
              <FormLabel>Address line 3 (optional)</FormLabel>
              <Input {...nullableTextRegister('pickupAddressLine3')} />
              <FormErrorMessage>{errors.pickupAddressLine3?.message}</FormErrorMessage>
            </FormControl>

            <HStack align="flex-start" spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
              <FormControl
                isInvalid={!!errors.pickupPostalCode}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.pickupPostalCode, 'Postal code')}
              >
                <FormLabel>Postal code</FormLabel>
                <Input {...nullableTextRegister('pickupPostalCode')} />
                <FormErrorMessage>{errors.pickupPostalCode?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.taxId}
                isRequired={isTaxIdRequired}
                {...getCopyFieldProps(defaultValues.taxId, 'Tax ID')}
              >
                <FormLabel>Tax ID</FormLabel>
                <Input {...nullableTextRegister('taxId')} />
                <FormErrorMessage>{errors.taxId?.message}</FormErrorMessage>
              </FormControl>
            </HStack>

            <Divider />

            <Text fontWeight={600}>Pickup schedule</Text>

            <FormControl
              isInvalid={!!errors.pickupDate}
              isRequired={true}
              {...getCopyFieldProps(defaultValues.pickupDate, 'Pickup date')}
            >
              <FormLabel>Pickup date (Mon-Wed preferred)</FormLabel>
              <Input type="date" {...register('pickupDate')} />
              <FormErrorMessage>{errors.pickupDate?.message}</FormErrorMessage>
            </FormControl>

            <HStack align="flex-start" spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
              <FormControl
                isInvalid={!!errors.pickupWindowStart}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.pickupWindowStart, 'Pickup window start')}
              >
                <FormLabel>Pickup window start</FormLabel>
                <Input type="time" step={60} {...register('pickupWindowStart')} />
                <FormErrorMessage>{errors.pickupWindowStart?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.pickupWindowEnd}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.pickupWindowEnd, 'Pickup window end')}
              >
                <FormLabel>Pickup window end</FormLabel>
                <Input type="time" step={60} {...register('pickupWindowEnd')} />
                <FormErrorMessage>{errors.pickupWindowEnd?.message}</FormErrorMessage>
              </FormControl>
            </HStack>

            <Text color="gray.600" fontSize="sm">
              Pickup window should be at least 2 hours. Carrier timing is indicative and can still vary based on local
              routing conditions.
            </Text>

            <Divider />

            <Text fontWeight={600}>Contents of package</Text>
            <VStack align="stretch" spacing={3}>
              {fields.map((field, index) => (
                <Box key={field.id} borderWidth="1px" borderColor="gray.200" borderRadius={6} p={3}>
                  <HStack align="flex-start" spacing={3} flexDirection={{ base: 'column', md: 'row' }}>
                    <FormControl
                      isInvalid={!!errors.items?.[index]?.description}
                      isRequired={true}
                      {...getCopyFieldProps(defaultValues.items[index]?.description, 'Item description')}
                    >
                      <FormLabel>Description</FormLabel>
                      <Input {...register(`items.${index}.description`, { required: true })} />
                      <FormErrorMessage>{errors.items?.[index]?.description?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl
                      isInvalid={!!errors.items?.[index]?.count}
                      isRequired={true}
                      maxW={{ md: 220 }}
                      {...getCopyFieldProps(defaultValues.items[index]?.count, 'Item count')}
                    >
                      <FormLabel>Count</FormLabel>
                      <Input
                        type="number"
                        step={1}
                        min={1}
                        {...register(`items.${index}.count`, {
                          setValueAs: value => {
                            if (value === '' || value == null) {
                              return null;
                            }
                            const numericValue = Number(value);
                            return Number.isFinite(numericValue) ? numericValue : null;
                          },
                        })}
                      />
                      <FormErrorMessage>{errors.items?.[index]?.count?.message}</FormErrorMessage>
                    </FormControl>
                    {!readOnly ? (
                      <Button
                        mt={{ base: 0, md: 8 }}
                        colorScheme="red"
                        variant="outline"
                        onClick={() => {
                          remove(index);
                        }}
                        isDisabled={fields.length <= 1}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </HStack>
                </Box>
              ))}
            </VStack>

            {!readOnly ? (
              <Button
                variant="outline"
                alignSelf="flex-start"
                size="sm"
                onClick={() => {
                  append({ description: '', count: 1 });
                }}
              >
                Add item
              </Button>
            ) : null}

            <Divider />

            <Text fontWeight={600}>Package dimensions</Text>
            <HStack align="flex-start" spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
              <FormControl
                isInvalid={!!errors.packageWeightKg}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.packageWeightKg, 'Package weight')}
              >
                <FormLabel>Weight (kg)</FormLabel>
                <Input type="number" step="any" {...nullableNumberRegister('packageWeightKg')} />
                <FormErrorMessage>{errors.packageWeightKg?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.packageLengthCm}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.packageLengthCm, 'Package length')}
              >
                <FormLabel>Length (cm)</FormLabel>
                <Input type="number" step="any" {...nullableNumberRegister('packageLengthCm')} />
                <FormErrorMessage>{errors.packageLengthCm?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.packageWidthCm}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.packageWidthCm, 'Package width')}
              >
                <FormLabel>Width (cm)</FormLabel>
                <Input type="number" step="any" {...nullableNumberRegister('packageWidthCm')} />
                <FormErrorMessage>{errors.packageWidthCm?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.packageHeightCm}
                isRequired={true}
                {...getCopyFieldProps(defaultValues.packageHeightCm, 'Package height')}
              >
                <FormLabel>Height (cm)</FormLabel>
                <Input type="number" step="any" {...nullableNumberRegister('packageHeightCm')} />
                <FormErrorMessage>{errors.packageHeightCm?.message}</FormErrorMessage>
              </FormControl>
            </HStack>

            <Text color="gray.600" fontSize="sm">
              Computed volume: {volumeCm3 == null ? 'n/a' : `${volumeCm3.toFixed(2)} cm³`}
            </Text>
            {shouldShowDefaultDnaGuidance ? (
              <Box
                borderWidth="1px"
                borderColor={defaultDnaSizeCheck.isAboveGuideline ? 'orange.300' : 'gray.200'}
                borderRadius={6}
                p={3}
                bg={defaultDnaSizeCheck.isAboveGuideline ? 'orange.50' : 'gray.50'}
              >
                <Text fontSize="sm" color="gray.700">
                  For standard 1.5 ml micro-centrifuge tubes (default DNA description), use this quick packing guide:
                </Text>
                <Table size="sm" variant="simple" mt={2}>
                  <Thead>
                    <Tr>
                      <Th>Tube count</Th>
                      <Th isNumeric={true}>Max volume (cm³)</Th>
                      <Th isNumeric={true}>Max weight (kg)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {DEFAULT_DNA_SIZE_GUIDELINES.map(guideline => (
                      <Tr key={`${guideline.minCount}-${guideline.maxCount}`}>
                        <Td>
                          {guideline.minCount}-{guideline.maxCount}
                        </Td>
                        <Td isNumeric={true}>{guideline.maxVolumeCm3.toLocaleString('en-US')}</Td>
                        <Td isNumeric={true}>{guideline.maxWeightKg.toFixed(1)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {defaultDnaSizeCheck.guideline ? (
                  <Text mt={2} fontSize="sm" color={defaultDnaSizeCheck.isAboveGuideline ? 'orange.700' : 'gray.700'}>
                    For your current count ({defaultDnaSizeCheck.defaultDnaItemCount}), keep package volume at or below{' '}
                    {defaultDnaSizeCheck.guideline.maxVolumeCm3.toLocaleString('en-US')} cm³ and weight at or below{' '}
                    {defaultDnaSizeCheck.guideline.maxWeightKg.toFixed(1)} kg.
                  </Text>
                ) : (
                  <Text mt={2} fontSize="sm" color="gray.700">
                    Guidance tiers are defined for 1-500 default tubes. If you have more, choose the smallest safe box
                    for transport.
                  </Text>
                )}
                {defaultDnaSizeCheck.guideline && defaultDnaSizeCheck.isAboveGuideline ? (
                  <Text mt={1} fontSize="sm" color="orange.700">
                    Current package details exceed the recommended volume or weight.
                  </Text>
                ) : null}
              </Box>
            ) : null}

            <Divider />

            {!readOnly ? (
              <HStack alignSelf="flex-start" spacing={3}>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={isSubmitting || formIsSubmitting || isConfirmingSubmit}
                >
                  {submitLabel}
                </Button>
                {extraButtons}
              </HStack>
            ) : null}
          </Stack>
        </fieldset>
      </form>
      <AlertDialog
        isOpen={isLargeBoxDialogOpen}
        leastDestructiveRef={largeBoxCancelRef}
        onClose={handleLargeBoxDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Check package size</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Text>
                  Attention: your shipment box seems larger than expected for the declared content. Please select the
                  smallest suitable box to reduce sample movement during transit. Is this box size still correct?
                </Text>
                {showAdditionalChargeWarning ? (
                  <Text color="red.600" fontWeight={700}>
                    This package is larger than the equivalent volume of standard shipping. Additional shipping charges
                    may apply.
                  </Text>
                ) : null}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={largeBoxCancelRef} onClick={handleLargeBoxDialogClose} isDisabled={isConfirmingSubmit}>
                Return
              </Button>
              <Button
                colorScheme="orange"
                ml={3}
                textTransform="none"
                isLoading={isConfirmingSubmit}
                onClick={() => {
                  if (!pendingLargeBoxValues) {
                    handleLargeBoxDialogClose();
                    return;
                  }
                  void (async () => {
                    setIsConfirmingSubmit(true);
                    try {
                      await onSubmit(pendingLargeBoxValues);
                    } finally {
                      setIsConfirmingSubmit(false);
                      handleLargeBoxDialogClose();
                    }
                  })();
                }}
              >
                Yes, the box is the correct size
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export function buildShipmentFormDefaults(input: {
  organisationId: number;
  labWorkbookId?: string | null;
  contactName: string;
  contactEmail: string;
  contactTelephoneNumber?: string | null;
  pickupCountry?: string | null;
  pickupAddressLine1?: string | null;
  pickupAddressLine2?: string | null;
  pickupAddressLine3?: string | null;
  pickupPostalCode?: string | null;
  pickupCity?: string | null;
  taxId?: string | null;
  pickupDate?: string;
  pickupWindowStart?: string;
  pickupWindowEnd?: string;
  packageWeightKg?: number | null;
  packageLengthCm?: number | null;
  packageWidthCm?: number | null;
  packageHeightCm?: number | null;
  items?: CreateShipmentRequest['items'];
  status?: ShipmentStatus;
}): ShipmentFormValues {
  return {
    organisationId: input.organisationId,
    labWorkbookId: input.labWorkbookId ?? null,
    status: input.status ?? ShipmentStatus.REQUESTED,
    pickupDate: input.pickupDate ?? '',
    pickupWindowStart: input.pickupWindowStart ?? '',
    pickupWindowEnd: input.pickupWindowEnd ?? '',
    contactName: input.contactName,
    contactEmail: input.contactEmail,
    contactTelephoneNumber: input.contactTelephoneNumber ?? null,
    pickupCountry: input.pickupCountry ?? null,
    pickupAddressLine1: input.pickupAddressLine1 ?? null,
    pickupAddressLine2: input.pickupAddressLine2 ?? null,
    pickupAddressLine3: input.pickupAddressLine3 ?? null,
    pickupPostalCode: input.pickupPostalCode ?? null,
    pickupCity: input.pickupCity ?? null,
    taxId: input.taxId ?? null,
    packageWeightKg: input.packageWeightKg ?? null,
    packageLengthCm: input.packageLengthCm ?? null,
    packageWidthCm: input.packageWidthCm ?? null,
    packageHeightCm: input.packageHeightCm ?? null,
    items:
      input.items && input.items.length > 0 ? input.items : [{ description: SAMPLE_DNA_ITEM_DESCRIPTION, count: 1 }],
  };
}
